import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  listarCatalogoDisponible,
  listarProductosInventario,
  crearProductoInventario,
  actualizarStockMinimo,
  listarMovimientosInventario,
  crearMovimientoInventario,
  crearTrasladoInventario,
  sincronizarMovimientoInventario,
  anularMovimientoInventario,
} from "../../services/inventarioService";
import { listarCentros } from "../../services/centrosService";
import { listarComprasContifico, obtenerDocumentoContifico } from "../../services/contificoService";
import { DetallePROModal } from "../rrhh/compras/ComprasRegistradas";
import { usePermissions } from "../../utils/permissions";
import { showToast } from "../../utils/showToast";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../styles/dashboardTheme.css";
import "../../styles/managementTheme.css";

// OJO: nunca usar new Date().toISOString().slice(0,10) para "hoy" — toISOString()
// convierte a UTC, y en Ecuador (UTC-5) desde las 19:00 ya marca el dia siguiente.
const hoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmt = (v) => Number(v || 0).toLocaleString("es-EC", { maximumFractionDigits: 2 });

// El kardex crece con cada entrega/ingreso — se pagina de verdad (nunca se
// carga todo de golpe), sin importar que tan angosto o amplio sea el filtro.
const PROD_PAGE_SIZE = 20;
const MOV_PAGE_SIZE = 50;

const TIPO_BADGE = {
  ING: { label: "Ingreso", cls: "bg-success-subtle text-success" },
  EGR: { label: "Egreso", cls: "bg-danger-subtle text-danger" },
};

const ORIGEN_LABEL = {
  compra: "Compra",
  ajuste_manual: "Ajuste manual",
  entrega_curso: "Entrega de curso",
  venta_extra: "Venta extra",
  traslado: "Traslado",
};

// ── Modal: vincular entregable como producto de inventario ────────────────────

function VincularProductoModal({ open, onClose, onCreado, catalogoDisponible }) {
  const [entregableId, setEntregableId] = useState("");
  const [stockMinimo, setStockMinimo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setEntregableId("");
      setStockMinimo("");
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!entregableId) return showToast({ type: "warning", message: "Selecciona un articulo del catalogo." });
    setSaving(true);
    try {
      const creado = await crearProductoInventario({
        entregable_id: Number(entregableId),
        stock_minimo: stockMinimo ? Number(stockMinimo) : 0,
      });
      showToast({ type: "success", message: "Producto vinculado a inventario." });
      onCreado?.(creado);
      onClose();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo vincular el producto." });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(15,23,42,0.45)" }} role="dialog">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header border-0">
              <h5 className="modal-title">
                <i className="bi bi-box-seam me-2 text-primary" />
                Vincular producto a inventario
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Articulo del catalogo <span className="text-danger">*</span></label>
                <select className="form-select" value={entregableId} onChange={(e) => setEntregableId(e.target.value)} required>
                  <option value="">Selecciona un articulo...</option>
                  {catalogoDisponible.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
                {!catalogoDisponible.length && (
                  <small className="text-muted d-block mt-1">
                    Todos los articulos del catalogo ya estan vinculados a inventario.
                  </small>
                )}
              </div>
              <div className="mb-1">
                <label className="form-label">Stock minimo (alerta de reposicion)</label>
                <input type="number" className="form-control" min="0" step="1" value={stockMinimo}
                  onChange={(e) => setStockMinimo(e.target.value)} placeholder="0 = sin alerta" />
              </div>
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn management-btn-primary" disabled={saving || !catalogoDisponible.length}>
                {saving ? "Guardando..." : "Vincular"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Modal: editar stock minimo de un producto ya vinculado ─────────────────────

function EditarStockMinimoModal({ producto, onClose, onGuardado }) {
  const [stockMinimo, setStockMinimo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (producto) setStockMinimo(String(producto.stock_minimo ?? 0));
  }, [producto]);

  if (!producto) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await actualizarStockMinimo(producto.id, Number(stockMinimo) || 0);
      showToast({ type: "success", message: "Stock minimo actualizado." });
      onGuardado?.();
      onClose();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo actualizar el stock minimo." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(15,23,42,0.45)" }} role="dialog">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header border-0">
              <h5 className="modal-title">
                <i className="bi bi-pencil-square me-2 text-primary" />
                Stock minimo — {producto.nombre}
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <label className="form-label">Stock minimo (alerta de reposicion)</label>
              <input type="number" className="form-control" min="0" step="1" value={stockMinimo}
                onChange={(e) => setStockMinimo(e.target.value)} placeholder="0 = sin alerta" autoFocus />
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn management-btn-primary" disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Modal: confirmar anulacion de un movimiento ────────────────────────────────

function AnularMovimientoModal({ movimiento, onClose, onConfirmar, anulando }) {
  if (!movimiento) return null;
  const badge = TIPO_BADGE[movimiento.tipo] || { label: movimiento.tipo, cls: "bg-secondary-subtle text-secondary" };

  return (
    <div className="modal fade show d-block management-modal" tabIndex="-1" role="dialog" aria-modal="true"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}>
      <div className="modal-dialog modal-md modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">Anular movimiento</h5>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
          </div>
          <div className="modal-body pt-0">
            <p className="mb-2">
              ¿Anular este <span className={`badge ${badge.cls}`}>{badge.label}</span> de{" "}
              <strong>{fmt(movimiento.cantidad)} {movimiento.producto_nombre}</strong>
              {movimiento.centro_nombre ? ` en ${movimiento.centro_nombre}` : ""}?
            </p>
            <p className="text-muted mb-0 small">
              No se borra del kardex: deja de contar para el saldo pero queda visible para
              trazabilidad. Es la forma correcta de corregir un error de captura — después
              registra el movimiento correcto.
            </p>
          </div>
          <div className="modal-footer border-0">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={anulando}>
              Cancelar
            </button>
            <button type="button" className="btn btn-danger" onClick={onConfirmar} disabled={anulando}>
              {anulando ? "Anulando..." : "Anular"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal: elegir una factura de compra ya registrada en Contifico ────────────
// Enlaza el ingreso a un Documento (PRO) real que ya existe en Contifico —
// haya entrado por Compras/Facturas recibidas de Ademy o directo desde
// contabilidad. Solo guarda el id (factura_compra_id); el detalle siempre se
// consulta en vivo con "Ver factura" en el kardex, para no duplicar datos que
// puedan cambiar del lado de Contifico.
const haceDias = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function SeleccionarFacturaModal({ open, onClose, onSeleccionar }) {
  const [desde, setDesde] = useState(haceDias(7));
  const [hasta, setHasta] = useState(hoy());
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [verDetalle, setVerDetalle] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listarComprasContifico({ fecha_inicial: desde, fecha_final: hasta, page_size: 100 });
      setLista(Array.isArray(res?.results) ? res.results : []);
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudieron cargar las facturas de Contífico." });
      setLista([]);
    } finally {
      setLoading(false);
    }
  }, [desde, hasta]);

  useEffect(() => {
    if (open) cargar();
  }, [open, cargar]);

  if (!open) return null;

  const texto = filtro.trim().toLowerCase();
  const filtrada = texto
    ? lista.filter(
        (d) =>
          d._proveedor?.nombre?.toLowerCase().includes(texto) ||
          d.descripcion?.toLowerCase().includes(texto) ||
          d.documento?.toLowerCase().includes(texto)
      )
    : lista;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(15,23,42,0.45)" }} role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header border-0">
              <h5 className="modal-title">
                <i className="bi bi-receipt me-2 text-primary" />
                Elegir factura de Contífico
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="d-flex gap-2 align-items-center flex-wrap mb-3">
                <div className="d-flex align-items-center gap-1">
                  <label className="form-label mb-0 me-1 text-muted small">Desde</label>
                  <input type="date" className="form-control form-control-sm" style={{ maxWidth: 150 }}
                    value={desde} onChange={(e) => setDesde(e.target.value)} />
                </div>
                <div className="d-flex align-items-center gap-1">
                  <label className="form-label mb-0 me-1 text-muted small">Hasta</label>
                  <input type="date" className="form-control form-control-sm" style={{ maxWidth: 150 }}
                    value={hasta} onChange={(e) => setHasta(e.target.value)} />
                </div>
                <button className="btn btn-outline-secondary btn-sm" onClick={cargar} disabled={loading}>
                  <i className="bi bi-arrow-repeat me-1" />Actualizar
                </button>
                <input type="text" className="form-control form-control-sm flex-grow-1" style={{ minWidth: 200 }}
                  placeholder="Buscar por proveedor, descripcion o N° documento..."
                  value={filtro} onChange={(e) => setFiltro(e.target.value)} />
              </div>

              {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
              ) : !filtrada.length ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-cloud fs-1 d-block mb-2 opacity-25" />
                  No se encontraron facturas en ese rango.
                </div>
              ) : (
                <div className="management-table-wrapper" style={{ maxHeight: 400, overflowY: "auto" }}>
                  <table className="table align-middle management-table mb-0">
                    <thead>
                      <tr>
                        <th>Proveedor</th>
                        <th>Descripción</th>
                        <th className="text-end">Total</th>
                        <th>Fecha</th>
                        <th>N° Documento</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtrada.map((d) => (
                        <tr key={d.id}>
                          <td>
                            <div className="fw-semibold">{d._proveedor?.nombre || "—"}</div>
                            {d._proveedor?.identificacion && (
                              <small className="text-muted">{d._proveedor.identificacion}</small>
                            )}
                          </td>
                          <td>{d.descripcion || "—"}</td>
                          <td className="text-end fw-semibold">${fmt(d.total)}</td>
                          <td>{d.fecha_emision || "—"}</td>
                          <td>
                            <button type="button" className="btn btn-link p-0 font-monospace"
                              onClick={() => setVerDetalle(d)} title="Ver factura">
                              {d.documento || "—"}
                            </button>
                          </td>
                          <td className="text-end text-nowrap">
                            <button type="button" className="btn btn-primary btn-sm"
                              onClick={() => { onSeleccionar(d); onClose(); }}
                              title="Usar esta factura">
                              <i className="bi bi-check-lg" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cerrar</button>
            </div>
          </div>
        </div>
      </div>
      <DetallePROModal doc={verDetalle} onClose={() => setVerDetalle(null)} />
    </>
  );
}

// ── Modal: nuevo movimiento (ingreso/egreso) ───────────────────────────────────

const FORM_INICIAL = {
  producto_inventario_id: "", centro_id: "", centro_destino_id: "", tipo: "ING", origen: "ajuste_manual",
  cantidad: "", costo_unitario: "", fecha: hoy(), descripcion: "", factura_compra_id: "",
};

function NuevoMovimientoModal({ open, onClose, onCreado, productos, centros }) {
  const [form, setForm] = useState(FORM_INICIAL);
  const [saving, setSaving] = useState(false);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [modalFactura, setModalFactura] = useState(false);
  const esTraslado = form.tipo === "TRA";
  const esCompra = !esTraslado && form.tipo === "ING" && form.origen === "compra";

  useEffect(() => {
    if (open) {
      setForm(FORM_INICIAL);
      setFacturaSeleccionada(null);
    }
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.producto_inventario_id) return showToast({ type: "warning", message: "Selecciona un producto." });
    if (!form.cantidad || Number(form.cantidad) <= 0) return showToast({ type: "warning", message: "La cantidad debe ser mayor a 0." });

    if (esTraslado) {
      if (!form.centro_id || !form.centro_destino_id) {
        return showToast({ type: "warning", message: "Selecciona el centro de origen y el de destino." });
      }
      if (form.centro_id === form.centro_destino_id) {
        return showToast({ type: "warning", message: "El centro de origen y destino no pueden ser el mismo." });
      }
    } else if (!form.centro_id) {
      return showToast({ type: "warning", message: "Selecciona un centro." });
    }

    setSaving(true);
    try {
      let creado;
      if (esTraslado) {
        creado = await crearTrasladoInventario({
          producto_inventario_id: Number(form.producto_inventario_id),
          centro_origen_id: Number(form.centro_id),
          centro_destino_id: Number(form.centro_destino_id),
          cantidad: Number(form.cantidad),
          fecha: form.fecha,
          descripcion: form.descripcion.trim() || null,
        });
      } else {
        creado = await crearMovimientoInventario({
          producto_inventario_id: Number(form.producto_inventario_id),
          centro_id: Number(form.centro_id),
          tipo: form.tipo,
          origen: form.origen,
          cantidad: Number(form.cantidad),
          costo_unitario: form.tipo === "ING" && form.costo_unitario ? Number(form.costo_unitario) : null,
          fecha: form.fecha,
          descripcion: form.descripcion.trim() || null,
          factura_compra_id: esCompra && form.factura_compra_id ? form.factura_compra_id : null,
        });
      }
      showToast({
        type: "success",
        message: esTraslado ? "Traslado registrado." : form.tipo === "ING" ? "Ingreso registrado." : "Egreso registrado.",
      });
      onCreado?.(creado);
      onClose();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo registrar el movimiento." });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(15,23,42,0.45)" }} role="dialog">
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header border-0">
              <h5 className="modal-title">
                <i className="bi bi-arrow-left-right me-2 text-primary" />
                Nuevo movimiento de inventario
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Tipo <span className="text-danger">*</span></label>
                  <div className="d-flex gap-3 flex-wrap">
                    {[
                      { v: "ING", label: "Ingreso (entra stock)" },
                      { v: "EGR", label: "Egreso (sale stock)" },
                      { v: "TRA", label: "Traslado (entre bodegas)" },
                    ].map(({ v, label }) => (
                      <div className="form-check" key={v}>
                        <input className="form-check-input" type="radio" id={`tipo-${v}`}
                          checked={form.tipo === v} onChange={() => set("tipo", v)} />
                        <label className="form-check-label" htmlFor={`tipo-${v}`}>{label}</label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Producto <span className="text-danger">*</span></label>
                  <select className="form-select" value={form.producto_inventario_id}
                    onChange={(e) => set("producto_inventario_id", e.target.value)} required>
                    <option value="">Selecciona un producto...</option>
                    {productos.map((p) => (
                      <option key={p.id} value={p.id}>{p.nombre}</option>
                    ))}
                  </select>
                </div>
                <div className={esTraslado ? "col-md-3" : "col-md-6"}>
                  <label className="form-label">
                    {esTraslado ? "Centro origen" : "Centro"} <span className="text-danger">*</span>
                  </label>
                  <select className="form-select" value={form.centro_id}
                    onChange={(e) => set("centro_id", e.target.value)} required>
                    <option value="">Selecciona un centro...</option>
                    {centros.map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
                {esTraslado && (
                  <div className="col-md-3">
                    <label className="form-label">Centro destino <span className="text-danger">*</span></label>
                    <select className="form-select" value={form.centro_destino_id}
                      onChange={(e) => set("centro_destino_id", e.target.value)} required>
                      <option value="">Selecciona un centro...</option>
                      {centros.map((c) => (
                        <option key={c.id} value={c.id}>{c.nombre}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className={form.tipo === "ING" ? "col-md-4" : "col-md-6"}>
                  <label className="form-label">Cantidad <span className="text-danger">*</span></label>
                  <input type="number" className="form-control" min="0.01" step="0.01"
                    value={form.cantidad} onChange={(e) => set("cantidad", e.target.value)} placeholder="50" />
                </div>
                {form.tipo === "ING" && (
                  <div className="col-md-4">
                    <label className="form-label">Costo unitario</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input type="number" className="form-control" min="0" step="0.01"
                        value={form.costo_unitario} onChange={(e) => set("costo_unitario", e.target.value)} placeholder="0.00" />
                    </div>
                  </div>
                )}
                <div className={form.tipo === "ING" ? "col-md-4" : "col-md-6"}>
                  <label className="form-label">Fecha</label>
                  <input type="date" className="form-control" value={form.fecha} onChange={(e) => set("fecha", e.target.value)} />
                </div>
                {!esTraslado && (
                  <div className="col-md-6">
                    <label className="form-label">Origen</label>
                    <select className="form-select" value={form.origen} onChange={(e) => set("origen", e.target.value)}>
                      <option value="ajuste_manual">Ajuste manual (conteo, compra sin factura aun, etc.)</option>
                      <option value="compra">Compra registrada</option>
                      {form.tipo === "EGR" && <option value="entrega_curso">Entrega incluida en curso</option>}
                      {form.tipo === "EGR" && <option value="venta_extra">Venta extra al estudiante</option>}
                    </select>
                  </div>
                )}
                {esCompra && (
                  <div className="col-12">
                    <label className="form-label">Factura de Contífico (opcional)</label>
                    {facturaSeleccionada ? (
                      <div className="d-flex align-items-center gap-2 border rounded p-2">
                        <i className="bi bi-receipt text-primary" />
                        <div className="flex-grow-1">
                          <div className="fw-semibold small">
                            {facturaSeleccionada._proveedor?.nombre || "—"} — ${fmt(facturaSeleccionada.total)}
                          </div>
                          <small className="text-muted font-monospace">
                            {facturaSeleccionada.documento} · {facturaSeleccionada.fecha_emision}
                          </small>
                        </div>
                        <button type="button" className="btn btn-outline-secondary btn-sm"
                          onClick={() => { setFacturaSeleccionada(null); set("factura_compra_id", ""); }}>
                          Quitar
                        </button>
                      </div>
                    ) : (
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setModalFactura(true)}>
                        <i className="bi bi-search me-1" />
                        Buscar factura en Contífico
                      </button>
                    )}
                    <small className="text-muted d-block mt-1">
                      Enlaza este ingreso a la factura real ya registrada en Contífico, para poder verla despues desde el kardex.
                    </small>
                  </div>
                )}
                <div className="col-12">
                  <label className="form-label">Descripcion</label>
                  <input className="form-control" value={form.descripcion}
                    onChange={(e) => set("descripcion", e.target.value)}
                    placeholder={esTraslado ? "Ej: Envio para curso nuevo en el otro centro" : "Ej: Compra de 50 camisetas talla variada, proveedor X"} />
                </div>
              </div>
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn management-btn-primary" disabled={saving}>
                {saving ? "Guardando..." : "Registrar"}
              </button>
            </div>
          </form>
        </div>
      </div>
      <SeleccionarFacturaModal
        open={modalFactura}
        onClose={() => setModalFactura(false)}
        onSeleccionar={(doc) => { setFacturaSeleccionada(doc); set("factura_compra_id", doc.id); }}
      />
    </div>
  );
}

// ── Paginacion (reutilizada en Stock actual y Kardex) ──────────────────────────

function PaginacionFooter({ page, pageSize, total, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;
  return (
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 px-3 py-2">
      <div className="text-muted small">
        Mostrando {(page - 1) * pageSize + 1}-{Math.min(total, page * pageSize)} de {total}
      </div>
      <div className="btn-group">
        <button className="btn btn-outline-secondary btn-sm" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
          <i className="bi bi-chevron-left" />
        </button>
        <button className="btn btn-outline-secondary btn-sm" disabled>
          Pagina {page} / {totalPages}
        </button>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
          <i className="bi bi-chevron-right" />
        </button>
      </div>
    </div>
  );
}

// ── Vista principal ────────────────────────────────────────────────────────────

export default function InventarioView() {
  const { hasPermission } = usePermissions();
  const canGestionar = hasPermission("inventario.gestionar");

  const [tab, setTab] = useState("productos");
  const [productos, setProductos] = useState([]);
  const [catalogoDisponible, setCatalogoDisponible] = useState([]);
  const [centros, setCentros] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMovs, setLoadingMovs] = useState(false);
  const [sincronizandoId, setSincronizandoId] = useState(null);
  const [movimientoAAnular, setMovimientoAAnular] = useState(null);
  const [anulando, setAnulando] = useState(false);
  const [facturaVista, setFacturaVista] = useState(null);
  const [modalVincular, setModalVincular] = useState(false);
  const [modalMovimiento, setModalMovimiento] = useState(false);
  const [editandoStockMinimo, setEditandoStockMinimo] = useState(null);
  const [filtroProducto, setFiltroProducto] = useState("");
  const [filtroCentro, setFiltroCentro] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroFechaInicial, setFiltroFechaInicial] = useState("");
  const [filtroFechaFinal, setFiltroFechaFinal] = useState("");
  const [prodPage, setProdPage] = useState(1);
  const [movPage, setMovPage] = useState(1);
  const [movTotal, setMovTotal] = useState(0);

  // Si todos los movimientos visibles son del mismo centro (un solo centro
  // registrado, o ya se filtro a uno), no tiene sentido repetir el nombre
  // (largo) en cada fila — se muestra una sola vez arriba de la tabla. Si
  // hay mas de uno mezclados (vista global con varios centros reales), se
  // mantiene la columna para poder distinguir cada fila.
  const centrosEnVista = useMemo(
    () => [...new Set(movimientos.map((m) => m.centro_nombre).filter(Boolean))],
    [movimientos]
  );
  const mostrarColumnaCentro = centrosEnVista.length > 1;

  const cargarProductos = useCallback(async () => {
    setLoading(true);
    try {
      const [prods, catalogo] = await Promise.all([
        listarProductosInventario(),
        listarCatalogoDisponible(),
      ]);
      setProductos(Array.isArray(prods) ? prods : []);
      setCatalogoDisponible(Array.isArray(catalogo) ? catalogo : []);
    } catch {
      showToast({ type: "danger", message: "No se pudieron cargar los productos de inventario." });
    } finally {
      setLoading(false);
    }
  }, []);

  // "Stock actual" no crece igual que el kardex (esta acotado por el tamaño
  // del catalogo), asi que se pagina del lado del cliente sobre la lista
  // completa — sin eso, el selector "Producto" del Kardex se quedaria sin
  // opciones fuera de la pagina actual.
  const productosPagina = useMemo(
    () => productos.slice((prodPage - 1) * PROD_PAGE_SIZE, prodPage * PROD_PAGE_SIZE),
    [productos, prodPage]
  );

  const cargarMovimientos = useCallback(async () => {
    setLoadingMovs(true);
    try {
      const data = await listarMovimientosInventario({
        producto_inventario_id: filtroProducto || undefined,
        centro_id: filtroCentro || undefined,
        tipo: filtroTipo || undefined,
        fecha_inicial: filtroFechaInicial || undefined,
        fecha_final: filtroFechaFinal || undefined,
        page: movPage,
        page_size: MOV_PAGE_SIZE,
      });
      setMovimientos(Array.isArray(data?.results) ? data.results : []);
      setMovTotal(data?.total || 0);
    } catch {
      showToast({ type: "danger", message: "No se pudieron cargar los movimientos." });
    } finally {
      setLoadingMovs(false);
    }
  }, [filtroProducto, filtroCentro, filtroTipo, filtroFechaInicial, filtroFechaFinal, movPage]);

  // Cualquier cambio de filtro (no de pagina) vuelve a la pagina 1 — evita
  // quedar "varado" en una pagina 4 que ya no existe con el nuevo filtro.
  useEffect(() => {
    setMovPage(1);
  }, [filtroProducto, filtroCentro, filtroTipo, filtroFechaInicial, filtroFechaFinal]);

  const verFacturaVinculada = async (facturaId) => {
    try {
      const doc = await obtenerDocumentoContifico(facturaId);
      setFacturaVista(doc);
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo cargar la factura desde Contífico." });
    }
  };

  const handleSincronizar = async (movimiento) => {
    setSincronizandoId(movimiento.id);
    try {
      await sincronizarMovimientoInventario(movimiento.id);
      showToast({ type: "success", message: "Movimiento sincronizado con Contífico." });
      cargarMovimientos();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo sincronizar con Contífico." });
    } finally {
      setSincronizandoId(null);
    }
  };

  const confirmarAnular = async () => {
    if (!movimientoAAnular) return;
    setAnulando(true);
    try {
      await anularMovimientoInventario(movimientoAAnular.id);
      showToast({ type: "success", message: "Movimiento anulado. Ya puedes registrar el correcto." });
      cargarProductos();
      cargarMovimientos();
      setMovimientoAAnular(null);
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo anular el movimiento." });
    } finally {
      setAnulando(false);
    }
  };

  useEffect(() => { cargarProductos(); }, [cargarProductos]);
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(productos.length / PROD_PAGE_SIZE));
    setProdPage((p) => Math.min(p, totalPages));
  }, [productos.length]);
  useEffect(() => {
    listarCentros().then((data) => setCentros(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (tab === "movimientos") cargarMovimientos();
  }, [tab, cargarMovimientos]);

  return (
    <section className="management-page management-theme-blue">
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">Inventario</p>
          <h4 className="management-title mb-1 d-flex align-items-center gap-2">
            <i className="bi bi-box-seam" />
            Existencias
          </h4>
          <p className="text-muted mb-0">
            Stock y kardex de articulos entregables (camisetas, mochilas, etc.) por centro.
            Para agregar un articulo nuevo al catalogo, ve a{" "}
            <Link to="/dashboard/configuracion/entregables">Configuracion &gt; Entregables</Link>.
          </p>
        </div>
        {canGestionar && (
          <div className="d-flex gap-2">
            {tab === "productos" && (
              <button type="button" className="btn management-btn-primary" onClick={() => setModalVincular(true)}>
                <i className="bi bi-link-45deg me-2" />
                Vincular producto
              </button>
            )}
            {tab === "movimientos" && (
              <button type="button" className="btn management-btn-primary" onClick={() => setModalMovimiento(true)}
                disabled={!productos.length}>
                <i className="bi bi-plus-circle me-2" />
                Nuevo movimiento
              </button>
            )}
          </div>
        )}
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === "productos" ? "active" : ""}`} onClick={() => setTab("productos")}>
            <i className="bi bi-boxes me-2" />
            Stock actual
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === "movimientos" ? "active" : ""}`} onClick={() => setTab("movimientos")}>
            <i className="bi bi-clock-history me-2" />
            Kardex
          </button>
        </li>
      </ul>

      {tab === "productos" && (
        <div className="management-card">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
          ) : !productos.length ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-box-seam fs-1 d-block mb-2 opacity-25" />
              Todavia no hay productos vinculados a inventario.
            </div>
          ) : (
            <div className="management-table-wrapper">
              <table className="table align-middle management-table mb-0">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th className="text-end">Stock global</th>
                    <th>Stock por centro</th>
                    <th className="text-end">Stock minimo</th>
                    {canGestionar && <th className="text-end">Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {productosPagina.map((p) => {
                    const bajoStock = p.stock_minimo > 0 && p.stock_global <= p.stock_minimo;
                    return (
                      <tr key={p.id}>
                        <td className="fw-semibold">{p.nombre}</td>
                        <td className="text-end">
                          <span className={`badge rounded-pill px-3 py-2 ${bajoStock ? "bg-danger-subtle text-danger" : "bg-success-subtle text-success"}`}>
                            {fmt(p.stock_global)}
                          </span>
                        </td>
                        <td>
                          {p.stock_por_centro.length ? (
                            <div className="d-flex flex-wrap gap-1">
                              {p.stock_por_centro.map((c) => (
                                <span key={c.bodega_id} className="badge bg-light text-dark border">
                                  {c.centro_nombre}: {fmt(c.saldo)}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted small">Sin movimientos</span>
                          )}
                        </td>
                        <td className="text-end text-muted">{p.stock_minimo > 0 ? fmt(p.stock_minimo) : "—"}</td>
                        {canGestionar && (
                          <td className="text-end">
                            <button type="button" className="btn btn-outline-secondary btn-sm"
                              title="Editar stock minimo" onClick={() => setEditandoStockMinimo(p)}>
                              <i className="bi bi-pencil-square" />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <PaginacionFooter page={prodPage} pageSize={PROD_PAGE_SIZE} total={productos.length} onPageChange={setProdPage} />
            </div>
          )}
        </div>
      )}

      {tab === "movimientos" && (
        <>
          <div className="management-card mb-3">
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <select className="form-select form-select-sm" style={{ maxWidth: 220 }}
                value={filtroProducto} onChange={(e) => setFiltroProducto(e.target.value)}>
                <option value="">Todos los productos</option>
                {productos.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
              <select className="form-select form-select-sm" style={{ maxWidth: 200 }}
                value={filtroCentro} onChange={(e) => setFiltroCentro(e.target.value)}>
                <option value="">Todos los centros</option>
                {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
              <select className="form-select form-select-sm" style={{ maxWidth: 160 }}
                value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                <option value="">Ingresos y egresos</option>
                <option value="ING">Solo ingresos</option>
                <option value="EGR">Solo egresos</option>
              </select>
              <div className="d-flex align-items-center gap-1">
                <label className="form-label mb-0 me-1 text-muted small">Desde</label>
                <input type="date" className="form-control form-control-sm" style={{ maxWidth: 150 }}
                  value={filtroFechaInicial} onChange={(e) => setFiltroFechaInicial(e.target.value)} />
              </div>
              <div className="d-flex align-items-center gap-1">
                <label className="form-label mb-0 me-1 text-muted small">Hasta</label>
                <input type="date" className="form-control form-control-sm" style={{ maxWidth: 150 }}
                  value={filtroFechaFinal} onChange={(e) => setFiltroFechaFinal(e.target.value)} />
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={cargarMovimientos} disabled={loadingMovs}>
                <i className="bi bi-arrow-repeat me-1" />Actualizar
              </button>
            </div>
          </div>
          <div className="management-card">
            {loadingMovs ? (
              <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
            ) : !movimientos.length ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-clock-history fs-1 d-block mb-2 opacity-25" />
                No hay movimientos registrados para este filtro.
              </div>
            ) : (
              <>
                {centrosEnVista.length === 1 && (
                  <div className="px-3 pt-3 pb-1 text-muted small d-flex align-items-center gap-1">
                    <i className="bi bi-geo-alt" />
                    Centro: <strong className="text-dark">{centrosEnVista[0]}</strong>
                  </div>
                )}
                {centrosEnVista.length > 1 && (
                  <div className="px-3 pt-3 pb-1 text-muted small d-flex align-items-center gap-1">
                    <i className="bi bi-globe" />
                    Vista global — {centrosEnVista.length} centros con movimientos
                  </div>
                )}
              <div className="management-table-wrapper">
                <table className="table align-middle management-table mb-0">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Producto</th>
                      {mostrarColumnaCentro && <th>Centro</th>}
                      <th>Tipo</th>
                      <th>Origen</th>
                      <th className="text-end">Cantidad</th>
                      <th className="text-end">Saldo</th>
                      <th>Descripcion</th>
                      <th>Registrado por</th>
                      {canGestionar && <th>Acciones</th>}
                      {canGestionar && <th>Contífico</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {movimientos.map((m) => {
                      const badge = TIPO_BADGE[m.tipo] || { label: m.tipo, cls: "bg-secondary-subtle text-secondary" };
                      const anulado = m.estado !== "confirmado";
                      const sincronizando = sincronizandoId === m.id;
                      return (
                        <tr key={m.id} className={anulado ? "opacity-50" : ""}>
                          <td>{m.fecha ? String(m.fecha).substring(0, 10) : "—"}</td>
                          <td className="fw-semibold">{m.producto_nombre}</td>
                          {mostrarColumnaCentro && <td>{m.centro_nombre}</td>}
                          <td>
                            <span className={`badge ${badge.cls}`}>{badge.label}</span>
                            {anulado && <span className="badge bg-secondary-subtle text-muted ms-1">Anulado</span>}
                          </td>
                          <td>
                            {ORIGEN_LABEL[m.origen] || m.origen}
                            {m.origen === "traslado" && m.centro_destino_nombre && (
                              <small className="d-block text-muted">
                                {m.tipo === "EGR" ? "→ " : "← "}
                                {m.centro_destino_nombre}
                              </small>
                            )}
                            {m.factura_compra_id && (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 d-block"
                                style={{ fontSize: 12 }}
                                onClick={() => verFacturaVinculada(m.factura_compra_id)}
                              >
                                <i className="bi bi-receipt me-1" />Ver factura
                              </button>
                            )}
                          </td>
                          <td className={`text-end ${anulado ? "text-decoration-line-through" : ""}`}>{fmt(m.cantidad)}</td>
                          <td className="text-end fw-semibold">{anulado ? "—" : fmt(m.saldo)}</td>
                          <td className="text-muted">{m.descripcion || "—"}</td>
                          <td className="text-muted">{m.registrado_por_nombre || "—"}</td>
                          {canGestionar && (
                            <td>
                              {anulado ? (
                                <span className="text-muted small">—</span>
                              ) : m.entrega_id ? (
                                <span
                                  className="text-muted small"
                                  title="Se genero al marcar un entregable como entregado — anulalo desde el tab Entregables del estudiante."
                                >
                                  <i className="bi bi-lock" />
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => setMovimientoAAnular(m)}
                                  title="Anular movimiento"
                                >
                                  <i className="bi bi-x-circle" />
                                </button>
                              )}
                            </td>
                          )}
                          {canGestionar && (
                            <td>
                              {anulado ? (
                                <span className="text-muted small">—</span>
                              ) : m.contifico_movimiento_id ? (
                                <span className="badge bg-success-subtle text-success" title={m.contifico_movimiento_id}>
                                  <i className="bi bi-cloud-check-fill me-1" />Sincronizado
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  disabled={sincronizando}
                                  onClick={() => handleSincronizar(m)}
                                  title="Enviar a Contífico (Bodega + Producto + Movimiento de Inventario)"
                                >
                                  {sincronizando
                                    ? <span className="spinner-border spinner-border-sm" />
                                    : <><i className="bi bi-cloud-upload me-1" />Enviar</>}
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <PaginacionFooter page={movPage} pageSize={MOV_PAGE_SIZE} total={movTotal} onPageChange={setMovPage} />
              </>
            )}
          </div>
        </>
      )}

      <VincularProductoModal
        open={modalVincular} onClose={() => setModalVincular(false)}
        onCreado={() => cargarProductos()}
        catalogoDisponible={catalogoDisponible}
      />
      <NuevoMovimientoModal
        open={modalMovimiento} onClose={() => setModalMovimiento(false)}
        onCreado={() => { cargarProductos(); if (tab === "movimientos") cargarMovimientos(); }}
        productos={productos} centros={centros}
      />
      <EditarStockMinimoModal
        producto={editandoStockMinimo}
        onClose={() => setEditandoStockMinimo(null)}
        onGuardado={cargarProductos}
      />
      <AnularMovimientoModal
        movimiento={movimientoAAnular}
        onClose={() => setMovimientoAAnular(null)}
        onConfirmar={confirmarAnular}
        anulando={anulando}
      />
      <DetallePROModal doc={facturaVista} onClose={() => setFacturaVista(null)} />
    </section>
  );
}
